import { ChapterType } from 'Types/ExerciseData';
import type { ChapterData, InteractiveEvent, Timestamp } from 'Types/ExerciseData';

/**
 * Scheduling primitives for tutorial video events (MUS-184).
 *
 * The tutorial drives Phaser from timestamps embedded in the video. The
 * original matcher only fired an event when an `onProgress` observation
 * happened to land inside a 200ms window around it, so a stalled main
 * thread — Phaser plus video decode routinely starve the progress timer
 * for seconds at a time — silently dropped the event and the video ran
 * straight through the exercise it was supposed to pause for.
 *
 * Everything here is edge-triggered instead: an event is due once the
 * playhead is at or past its timestamp and it has not fired yet. Late
 * observations recover the event rather than missing it.
 */

/** Parse a `M:SS.ss` tutorial timestamp into absolute seconds. */
export function timestampToSeconds(stamp: Timestamp): number {
  const [minutes, seconds] = stamp.split(':').map((part) => parseFloat(part));
  return minutes * 60 + seconds;
}

export interface ScheduledEvent {
  /** Stable identity used for de-duplication. */
  key: string;
  /** Index of the chapter this event is defined in. */
  chapterIdx: number;
  /** Absolute video position, in seconds. */
  at: number;
  event: InteractiveEvent;
}

/**
 * Identity for a scheduled event. Keyed on chapter *and* timestamp: two
 * chapters may legitimately carry events at the same offset, and a single
 * scalar "last event time" cannot tell them apart.
 */
export function scheduledEventKey(chapterIdx: number, at: Timestamp): string {
  return `${chapterIdx}@${at}`;
}

/** Flatten every exercise chapter's events into one chronological schedule. */
export function buildEventSchedule(chapters: ChapterData[]): ScheduledEvent[] {
  return chapters
    .flatMap((chapter, chapterIdx) => {
      const [type, data] = chapter.data;
      if (type !== ChapterType.Exercise || !data) return [];
      return data.events.map((event) => ({
        key: scheduledEventKey(chapterIdx, event.at),
        chapterIdx,
        at: timestampToSeconds(event.at),
        event,
      }));
    })
    .sort((a, b) => a.at - b.at);
}

/**
 * Every event the playhead has reached that has not fired yet, oldest
 * first. Unlike a fixed-width match window this cannot miss an event
 * because the observation that would have caught it arrived late.
 */
export function selectDueEvents(
  schedule: ScheduledEvent[],
  currentTime: number,
  fired: ReadonlySet<string>,
): ScheduledEvent[] {
  return schedule.filter((item) => item.at <= currentTime && !fired.has(item.key));
}

/**
 * Trim a due-event list at the first event that pauses.
 *
 * A stall long enough to skip several events leaves them all due at once,
 * but anything sequenced after a pause is meant to happen once the user
 * has finished the exercise — not immediately, into a paused scene.
 */
export function takeThroughFirstPause(due: ScheduledEvent[]): ScheduledEvent[] {
  const pauseIdx = due.findIndex((item) => item.event.pause);
  return pauseIdx === -1 ? due : due.slice(0, pauseIdx + 1);
}

/**
 * The keys to treat as already handled when the playhead lands at
 * `seconds` by way of a seek, so jumping forward doesn't replay every
 * event that was skipped over.
 *
 * Inclusive of events exactly at `seconds`: the pause path rewinds to an
 * event's own timestamp, and that event must stay suppressed rather than
 * immediately re-firing.
 */
export function keysHandledBefore(schedule: ScheduledEvent[], seconds: number): Set<string> {
  return new Set(schedule.filter((item) => item.at <= seconds).map((item) => item.key));
}

/**
 * Index of the chapter containing `currentTime`. Deliberately has no
 * lookahead: event dispatch must attribute an event to the chapter it
 * actually belongs to, not to the chapter that is about to start.
 */
export function chapterIndexAtTime(chapters: ChapterData[], currentTime: number): number {
  return chapters.reduce(
    (best, chapter, idx) => (timestampToSeconds(chapter.start) <= currentTime ? idx : best),
    0,
  );
}

/**
 * The chapter the exercise on screen belongs to, for crediting a pass or
 * working out what follows a skip.
 *
 * The playhead is not a reliable witness. The narration for a later objective
 * can carry it to within the one-second lookahead of the next chapter while
 * this chapter's exercise is still on screen, and the focused chapter follows
 * the playhead. The chapter the running scene was loaded for does not move.
 */
export function chapterOfActiveExercise(
  sceneChapter: number | undefined,
  focusedChapter: number | undefined,
): number {
  return sceneChapter ?? focusedChapter ?? 0;
}
